import React, { Fragment, useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Button,
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Modal,
  NamedLink,
  NamedRedirect,
  Page,
  PrimaryButton,
} from '../../components';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import css from './CheckEligibilityPage.css';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import goLogo from '../../assets/logos/GoLogo.png';
import goLogo2x from '../../assets/logos/GoLogo@2x.png';
import Loading from './Loading';
import {
  checkPaidDeposit,
  DRIVELAH_GO_ELIGIBILITY_CHECK_NOTIFY_REQUESTED,
  REQUEST_APPOINTMENT as REQUEST_APPOINTMENT_EVENT_TYPE,
  sendCheckEligibilityStatus,
  START_CHECK_ELIGIBILITY,
} from '../../util/checkEligibility';
import { fetchCurrentUser, updateUserData } from '../../ducks/user.duck';
import { PaymentDepositForm } from '../../forms';
import { initiateOrder } from './CheckEligibilityPage.duck';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import classNames from 'classnames';
import { ensurePaymentMethodCard, ensureStripeCustomer } from '../../util/data';
import config from '../../config';

const CHECK = 'check';
const REQUEST_APPOINTMENT = 'appointment';
const DEPOSIT = 'deposit';

const getPaymentMethod = (selectedPaymentMethod, hasDefaultPaymentMethod) => {
  return selectedPaymentMethod == null && hasDefaultPaymentMethod
    ? 'defaultCard'
    : selectedPaymentMethod == null
      ? 'onetimeCardPayment'
      : selectedPaymentMethod;
};

const pageTitle = (tab, intl) => {
  let title = '';

  return title;
};

const CheckEligibilityPageComponent = props => {
  const {
    intl,
    params,
    scrollingDisabled,
    currentUser,
    onUpdateUserData,
    onHandlePayment,
    initiateOrderInProgress,
    history,
    initiateOrderError,
    onManageDisableScrolling,
  } = props;
  const { tab } = params;
  const [acceptTerm, setAcceptTerm] = useState(true);
  const [submited, setSubmited] = useState(false);
  const [paidDeposit, setPaidDeposit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const title = pageTitle(tab, intl);
  const stripeRef = useRef();

  const setupStripe = () => {
    if (!window.Stripe) {
      return false;
    }

    if (config.stripe.publishableKey && !stripeRef.current) {
      stripeRef.current = window.Stripe(config.stripe.publishableKey);
    }
    return true;
  };

  useEffect(() => {
    if (tab === DEPOSIT && currentUser && currentUser.id) {
      setLoading(true);
      checkPaidDeposit({ userId: currentUser.id.uuid }).then(response => {
        const { depositDone } = response;
        setPaidDeposit(depositDone);
        setLoading(false);
      });
    }
  }, [currentUser, tab]);

  useEffect(() => {
    // Wait for Stripe to be loaded
    if (tab === DEPOSIT) {
      const checkStripeLoaded = () => {
        if (setupStripe()) {
          setStripeLoading(false);
          return true;
        }
        return false;
      };

      // Try to setup Stripe immediately
      if (checkStripeLoaded()) {
        return;
      }

      // If not loaded yet, poll every 100ms for up to 10 seconds
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds total
      const interval = setInterval(() => {
        attempts++;
        if (checkStripeLoaded()) {
          clearInterval(interval);
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.error('Stripe failed to load within 10 seconds');
          setStripeLoading(false);
        }
      }, 100);

      return () => clearInterval(interval);
    } else {
      setStripeLoading(false);
    }
  }, [tab]);

  const handleStartCheck = () => {
    const { id, attributes } = currentUser;
    const { email } = attributes;
    setLoading(true);
    return sendCheckEligibilityStatus(
      { userId: id.uuid, userEmail: email },
      START_CHECK_ELIGIBILITY
    )
      .then(() => {
        return props.fetchCurrentUser().then(() => {
          setLoading(false);
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleNotifyMe = () => {
    const { id, attributes } = currentUser;
    const { email } = attributes;
    setLoading(true);
    return sendCheckEligibilityStatus(
      { userId: id.uuid, userEmail: email },
      DRIVELAH_GO_ELIGIBILITY_CHECK_NOTIFY_REQUESTED
    )
      .then(() => {
        setLoading(false);
        setSubmited(true);
      })
      .catch(e => {
        console.error(e);
        setLoading(false);
      });
  };

  const handleRequestAppointment = () => {
    const { id, attributes } = currentUser;
    const { email } = attributes;

    return sendCheckEligibilityStatus(
      { userId: id.uuid, userEmail: email },
      REQUEST_APPOINTMENT_EVENT_TYPE
    ).then(() => {
      onUpdateUserData({
        protectedData: {
          checkEligibleStatus: 'request-appointment',
        },
      }).catch(e => console.error(e));
    });
  };

  const pageContent = () => {
    if (!currentUser || !currentUser.id) return null;
    let content = null;
    const { displayName, protectedData, metadata = {} } = currentUser.attributes.profile;
    const { checkEligibleStatus } = protectedData || {};
    const { drivelahGoEligibilityCheckStatus, drivelahGoEligibilityNotifyMePressed } = metadata;
    switch (tab) {
      case CHECK: {
        const isInProgress = !!(
          drivelahGoEligibilityCheckStatus && drivelahGoEligibilityCheckStatus === 'pending'
        );
        const isDecline = !!(
          drivelahGoEligibilityCheckStatus && drivelahGoEligibilityCheckStatus === 'declined'
        );

        content = isDecline ? (
          <Fragment>
            <div className={css.title}>
              <FormattedMessage
                id="CheckEligibilityPage.declined.title1"
                values={{ displayName }}
              />
              <br />
              <FormattedMessage id="CheckEligibilityPage.declined.title2" />
            </div>
            <div className={css.description}>
              <FormattedMessage id="CheckEligibilityPage.declined.description1" />
            </div>

            <Button
              className={classNames(css.button, css.yellowButton)}
              onClick={drivelahGoEligibilityNotifyMePressed ? () => { } : handleNotifyMe}
              inProgress={loading}
            >
              {submited || drivelahGoEligibilityNotifyMePressed ? (
                <FormattedMessage id="CheckEligibilityPage.declined.receiveYourRequest" />
              ) : (
                <FormattedMessage id="CheckEligibilityPage.declined.pleaseNotifyMe" />
              )}
            </Button>
          </Fragment>
        ) : (
          <Fragment>
            {isInProgress ? (
              <Fragment>
                <div className={css.title}>
                  <FormattedMessage
                    id="CheckEligibilityPage.checkInProgress.title1"
                    values={{ displayName }}
                  />
                  <br />
                  <FormattedMessage id="CheckEligibilityPage.checkInProgress.title2" />
                </div>
                <div className={css.loadingWrapper}>
                  <Loading />
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <div className={css.title}>
                  <FormattedMessage
                    id="CheckEligibilityPage.check.title1"
                    values={{ displayName }}
                  />
                  <br />
                  <FormattedMessage id="CheckEligibilityPage.check.title2" />
                </div>
                <div className={css.description}>
                  <FormattedMessage id="CheckEligibilityPage.check.description1" />
                  <br />
                  <br />
                  <FormattedMessage id="CheckEligibilityPage.check.description2" />
                </div>

                <Button className={css.button} onClick={handleStartCheck} inProgress={loading}>
                  <FormattedMessage id="CheckEligibilityPage.check.startCheck" />
                </Button>
              </Fragment>
            )}
          </Fragment>
        );
        break;
      }
      case REQUEST_APPOINTMENT: {
        const requested = !!(checkEligibleStatus && checkEligibleStatus === 'request-appointment');
        content = (
          <Fragment>
            {requested ? (
              <Fragment>
                <div className={css.title}>
                  <FormattedMessage id="CheckEligibilityPage.requestedAppointment.title" />
                </div>
                <div className={css.description}>
                  <FormattedMessage id="CheckEligibilityPage.requestedAppointment.description" />
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <div className={css.title}>
                  <FormattedMessage
                    id="CheckEligibilityPage.requestAppointment.title1"
                    values={{ displayName }}
                  />
                  <br />
                  <FormattedMessage id="CheckEligibilityPage.requestAppointment.title2" />
                </div>
                <div className={css.description}>
                  <FormattedMessage id="CheckEligibilityPage.requestAppointment.description" />
                </div>
                <div className={css.button} onClick={handleRequestAppointment}>
                  <FormattedMessage id="CheckEligibilityPage.requestAppointment" />
                </div>
              </Fragment>
            )}
          </Fragment>
        );
        break;
      }
      case DEPOSIT: {
        const hasDefaultPaymentMethod = !!(
          ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
          ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id
        );
        content = loading || stripeLoading ? (
          <Fragment>
            <div className={css.title}>
              {loading ? 'Checking...' : 'Loading payment system...'}
            </div>
          </Fragment>
        ) : submited || paidDeposit ? (
          <Fragment>
            <div className={css.title}>
              <FormattedMessage
                id="CheckEligibilityPage.submitedDeposit.title1"
                values={{ displayName }}
              />
              <br />
              <FormattedMessage id="CheckEligibilityPage.submitedDeposit.title2" />
            </div>
          </Fragment>
        ) : !stripeRef.current ? (
          <Fragment>
            <div className={css.title}>
              Unable to load payment system
            </div>
            <div className={css.description}>
              Please refresh the page or try again later. If the problem persists, please contact support.
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <Modal
              onManageDisableScrolling={onManageDisableScrolling}
              isOpen={acceptTerm}
              disableCloseBtn
            >
              <h2 className={css.title}>
                Drive lah Device plan agreement
              </h2>
              <div className={css.term}>
                <p className={css.description}>
                By proceeding, you confirm your acceptance of the following binding terms:
                </p>
                <ol className={css.termList}>
                  <li>Subscription & Fees
                    <ul>
                      <li>GPS device: $19/month | GPS + Lockbox device: $25/month</li>
                      <li>Installation: Home $30 | At our location free</li>
                      <li>A 4% processing fee applies to all payments</li>
                      <li>All fees are strictly non-refundable once paid</li>
                    </ul>
                  </li>
                  <li>Commitment
                    <ul>
                      <li>A minimum subscription period of 2 months applies from the installation date</li>
                      <li>Subscription fees remain payable in full regardless of usage, booking demand, or host performance</li>
                    </ul>
                  </li>
                  <li>Availability & Demand
                    <ul>
                    <li>Hosts are required to maintain at least 75% availability for booking requests</li>
                    <li>Any reduction in demand arising from host actions (including but not limited to blocking dates, declining or cancelling bookings, or restrictive listing settings) shall be the sole responsibility of the host</li>
                    <li>No refunds or credits shall be issued on the basis of low or no booking demand</li>
                    </ul>
                  </li>
                  <li>Device Responsibility
                    <ul>
                      <li>Devices remain the property of Drive lah and must be returned upon termination of subscription</li>
                      <li>If a device is lost, not returned, damaged, or tampered with, the host will be liable for a replacement fee of $250</li>
                    </ul>
                  </li>
                  <li>Data & Privacy
                    <ul>
                      <li>Drive lah may collect and use data generated through the device to improve services</li>
                      <li>Personally identifiable data will not be shared with third parties without consent, unless required by law</li>
                    </ul>
                  </li>
                  <li>Governing Law
                    <ul>
                      <li>This agreement shall be governed by and construed in accordance with the laws of Singapore</li>
                      <li>Both parties submit to the exclusive jurisdiction of the Singapore courts</li>
                    </ul>
                  </li>
                </ol>
                <p className={css.lastDescription}>
                By clicking “Confirm”, you confirm you have read, understood, and accepted the above terms.
                </p>
              </div>

              <PrimaryButton className={css.accept} onClick={() => setAcceptTerm(false)}>
                confirm
              </PrimaryButton>
              <div className={css.declineWrapper}>
                <NamedLink name="LandingPage" className={css.decline}>
                  decline
                </NamedLink>
              </div>
            </Modal>
            <div className={css.title}>
              <FormattedMessage id="CheckEligibilityPage.deposit.title" />
            </div>
            <div className={css.description}>
              <FormattedMessage id="CheckEligibilityPage.deposit.description" />
            </div>
            <PaymentDepositForm
              defaultPaymentMethod={
                hasDefaultPaymentMethod ? currentUser.stripeCustomer.defaultPaymentMethod : null
              }
              intl={intl}
              currentUser={currentUser}
              initiateOrderError={initiateOrderError}
              inProgress={initiateOrderInProgress}
              onSubmit={values => {
                const selectedPaymentMethod =
                  values.paymentMethod === 'defaultCard' && hasDefaultPaymentMethod
                    ? currentUser.stripeCustomer.defaultPaymentMethod.attributes
                      .stripePaymentMethodId
                    : null;
                return onHandlePayment(
                  values.card,
                  values.stripe || stripeRef.current,
                  selectedPaymentMethod,
                  values.replaceCurrentCard
                ).then(() => {
                  setSubmited(true);
                  onUpdateUserData({
                    protectedData: {
                      checkEligibleStatus: null,
                    },
                    privateData: {
                      preventRemoveCard: true,
                    },
                  });
                  setTimeout(() => {
                    const routes = routeConfiguration();
                    const url = createResourceLocatorString('LandingPage', routes);
                    history.replace(url);
                  }, 5000);
                });
              }}
            />
          </Fragment>
        );
        break;
      }
    }

    return content;
  };

  const content = pageContent();
  const currentUserLoaded = currentUser && currentUser.id;
  const { metadata = {} } = currentUser ? currentUser.attributes.profile : {};
  const { drivelahGoEligibilityCheckStatus, drivelahGoEligibilityNotifyMePressed } = metadata;
  const isConfirmed = !!(
    drivelahGoEligibilityCheckStatus && drivelahGoEligibilityCheckStatus === 'confirmed'
  );
  if (currentUserLoaded && isConfirmed && tab === CHECK) {
    return <NamedRedirect name="CheckEligibilityPage" params={{ tab: REQUEST_APPOINTMENT }} />;
  }

  if (currentUserLoaded && (tab === DEPOSIT || tab === REQUEST_APPOINTMENT) && !isConfirmed) {
    return <NamedRedirect name="CheckEligibilityPage" params={{ tab: CHECK }} />;
  }

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.background}>
          <div className={css.contentWrapper}>
            <img srcSet={`${goLogo} 1x, ${goLogo2x} 2x`} className={css.goLogo} />
            {content}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { initiateOrderInProgress, initiateOrderError } = state.CheckEligibilityPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    initiateOrderInProgress,
    initiateOrderError,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onUpdateUserData: data => dispatch(updateUserData(data)),
    onHandlePayment: (card, stripe, paymentMethod, replaceCurrentCard) =>
      dispatch(initiateOrder(card, stripe, paymentMethod, replaceCurrentCard)),
    fetchCurrentUser: () => dispatch(fetchCurrentUser()),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
  };
};

const CheckEligibilityPage = compose(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(CheckEligibilityPageComponent);

export default CheckEligibilityPage;
